import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  Cuestionario,
  ResumenVersion,
  ColorSeccion,
  EntradaGuardarPregunta,
  Escala,
  Pregunta,
  TipoPregunta,
  Version,
} from '@copathy/shared';
import {
  BARRA_MAXIMO,
  BARRA_MINIMO,
  COLORES_SECCION,
  FAMILIAS_PREGUNTA,
  HEX_SECCION,
  TIPOS_PREGUNTA,
  TIPO_PREGUNTA,
  admiteObservacion,
  esSeccion,
  usaEscala,
  usaExtremos,
  usaItems,
  usaRango,
} from '@copathy/shared';
import { Boton } from '../componentes/Boton';
import { CampoImagen, ImagenCuestionario } from '../componentes/ImagenCuestionario';
import { BotonVolver } from '../componentes/BotonVolver';
import { Campo } from '../componentes/Campo';
import { Combo } from '../componentes/Combo';
import { Modal } from '../componentes/Modal';
import { ModalEscala } from '../componentes/ModalEscala';
import { ModalSets } from '../componentes/ModalSets';
import { Contenedor } from '../componentes/Contenedor';
import { VistaFormulario } from '../componentes/VistaFormulario';
import { ACENTOS, acentoDeColor } from '../componentes/PreguntaRespondible';
import { Check, Copiar, Equis, Flecha, Lapiz, Reloj } from '../componentes/iconos';
import { useMigas } from '../lib/migas';
import { usePuede } from '../lib/auth';
import { ErrorApi } from '../lib/api';
import { useCatalogo, useGuardarOpcion } from '../lib/configuracion';
import {
  useBorrador,
  useActivarEscala,
  useCrearEscala,
  useCuestionario,
  useEditarCuestionario,
  useEditarEscala,
  useSets,
  usePortada,
  useEscalas,
  useVersion,
  useVersiones,
} from '../lib/cuestionarios';

/**
 * M5 — el constructor: herramienta a la izquierda, **un teléfono** a la derecha.
 *
 * ── La idea que gobierna la pantalla ──
 * **Lo publicado se mira; lo que se edita es siempre un borrador.** Si una
 * versión respondida se pudiera editar, el «antes vs. después» compararía cosas
 * distintas sin que nada falle. Lo impide el motor (D-049); acá se hace visible.
 *
 * ── Por qué el teléfono ──
 * El trabajador responde desde la app móvil (D-011). La previa dibuja el
 * formulario DENTRO de un teléfono, paginado y navegable, porque probar el
 * recorrido es probar el instrumento — y porque es el adelanto de la app móvil
 * que aún no existe: cuando exista, esto es lo que la valida.
 *
 * ── El selector de versiones es una frase, no chips ──
 * Los chips «v3 · borrador / v2 · publicada / v1 · retirada» parecían filtros y
 * nadie sabía para qué eran (lo dijo Jonathan). Ahora hay UNA insignia de estado
 * y, si hay más versiones, un desplegable que dice qué es cada una en palabras.
 */

export function CuestionarioArmar() {
  const { id: idTexto } = useParams();
  const id = Number(idTexto);

  const cuestionario = useCuestionario(id);
  const portada = usePortada(id);
  // Cambia con cada subida para que el binario se vuelva a pedir: reemplazar la
  // portada no cambia `tienePortada`, así que sin esto se seguiría viendo la
  // anterior hasta recargar la página.
  const [revisionPortada, setRevisionPortada] = useState(0);
  const versiones = useVersiones(id);
  const acciones = useBorrador(id);
  const escalas = useEscalas();
  const puedeArmar = usePuede('cuestionarios.armar');

  const [error, setError] = useState<string | null>(null);
  const [elegida, setElegida] = useState<number | null>(null);
  /*
    ── `original` no es redundante (revisión del 2026-09-07) ──
    Guarda el borrador tal como se abrió, para poder saber si hay cambios sin
    guardar. Sin esto no había forma de distinguir «abrí el editor y no toqué
    nada» de «escribí media pregunta», así que cerrar perdía lo escrito **en
    silencio**: la pregunta simplemente no aparecía y no había a quién preguntar.

    Es el mismo mecanismo que `hayCambios` en `FormularioCliente` y en el
    `Modal`: comparar contra el estado inicial. Se reutiliza el patrón —y el
    texto del aviso— en vez de inventar un tercero.
  */
  const [edicion, setEdicion] = useState<{
    preguntaId: number | null;
    f: Borrador;
    original: Borrador;
  } | null>(null);
  /*
    ── Elegir el tipo ANTES de escribir (revisión del 2026-09-07) ──
    «Agregar pregunta» creaba una likert y metía el selector de tipo dentro del
    editor. O sea: la decisión que más cambia el resultado —una matriz no se
    parece en nada a un texto libre— quedaba tomada por omisión y escondida entre
    los demás campos.

    Ahora el botón abre el selector. Se elige el tipo, y entonces se abre el
    editor ya con la forma correcta.
  */
  const [eligiendoTipo, setEligiendoTipo] = useState(false);

  const hayCambios = edicion
    ? JSON.stringify(edicion.f) !== JSON.stringify(edicion.original)
    : false;

  /**
   * Cierra o reemplaza la edición, preguntando si hay algo escrito sin guardar.
   * Devuelve `false` si la persona decidió quedarse.
   *
   * Todo lo que abandona el editor pasa por acá —cerrar, editar otra pregunta,
   * duplicar, agregar, cambiar de versión—: cada camino que lo esquive es un
   * camino por el que se pierde lo escrito, y son justo los que nadie prueba.
   */
  function dejarEdicion(siguiente: typeof edicion = null): boolean {
    if (hayCambios && !confirm('Hay cambios sin guardar. ¿Descartarlos?')) return false;
    setEdicion(siguiente);
    return true;
  }

  /*
    Qué versión se mira. Por omisión el borrador si hay uno y quien mira puede
    editar. `elegida` gana solo si apunta a una versión que todavía existe —
    descartar un borrador deja el id colgando de una fila borrada.
  */
  const lista = versiones.data ?? [];
  const porOmision =
    (puedeArmar ? lista.find((v) => v.estado === 'borrador') : undefined) ??
    lista.find((v) => v.estado === 'publicada') ??
    lista[0];
  const versionId =
    (elegida !== null && lista.some((v) => v.id === elegida) ? elegida : null) ??
    porOmision?.id ??
    null;

  const version = useVersion(id, versionId);

  useMigas([
    { texto: 'Cuestionarios', a: '/cuestionarios' },
    { texto: cuestionario.data?.nombre ?? 'Cuestionario' },
  ]);

  // Cambiar de versión con el editor abierto dejaría un formulario apuntando a
  // una pregunta de otra versión.
  useEffect(() => {
    /*
      Cambiar de versión con el editor abierto dejaría un formulario apuntando a
      una pregunta de otra versión. Se cierra sin preguntar **a propósito**: el
      cambio de versión ya ocurrió cuando este efecto corre, así que preguntar
      acá ofrecería una decisión que ya no se puede honrar. La pregunta va en el
      selector, antes de cambiar.
    */
    setEdicion(null);
    setEligiendoTipo(false);
  }, [versionId]);

  /*
    Lo que ve el teléfono: las preguntas guardadas, con la que se edita
    sustituida por su versión en vivo.

    ⚠️ Este hook va ANTES de los `return` de carga: un hook tras un `return`
    condicional tumba la pantalla con «Rendered more hooks...», y ni TypeScript
    ni el build lo ven — se descubrió abriendo la página.
  */
  /*
    Recargar o cerrar la pestaña con algo escrito. El navegador muestra SU
    diálogo —el texto no se puede elegir— y solo lo hace si hubo interacción con
    la página, que en este caso siempre la hubo: la persona estuvo escribiendo.

    ⚠️ Esto NO cubre la navegación dentro de la aplicación —un clic en el menú—:
    eso necesita un bloqueo del router y es un cambio de más peso. Queda
    anotado como lo que falta.
  */
  useEffect(() => {
    if (!hayCambios) return;
    const avisar = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [hayCambios]);

  const guardadas = version.data?.preguntas;
  const preguntasEnPantalla: Pregunta[] = useMemo(() => {
    const base = guardadas ?? [];
    if (!edicion) return base;
    const enVivo = aPreguntaPrevia(
      edicion.f,
      edicion.preguntaId ?? -1,
      0,
      base.find((p) => p.id === edicion.preguntaId)?.tieneImagen ?? false,
    );
    if (edicion.preguntaId === null) {
      return [...base, { ...enVivo, orden: base.length + 1 }];
    }
    return base.map((p) => (p.id === edicion.preguntaId ? { ...enVivo, orden: p.orden } : p));
  }, [guardadas, edicion]);

  const idResaltado = edicion ? (edicion.preguntaId ?? -1) : null;

  if (cuestionario.isPending || versiones.isPending || version.isPending) {
    return (
      <Contenedor ancho="ancho">
        <p className="py-16 text-center text-marino-400">Cargando…</p>
      </Contenedor>
    );
  }

  if (cuestionario.isError || !cuestionario.data || version.isError || !version.data) {
    return (
      <Contenedor ancho="ancho">
        <BotonVolver a="/cuestionarios">Cuestionarios</BotonVolver>
        <p className="py-16 text-center text-red-600">No encontramos este cuestionario.</p>
      </Contenedor>
    );
  }

  const c = cuestionario.data;
  const v: Version = version.data;
  const esBorrador = v.estado === 'borrador';

  async function ejecutar(fn: () => Promise<unknown>, generico: string) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : generico);
    }
  }

  async function ejecutarYMirar(fn: () => Promise<Version>, generico: string) {
    setError(null);
    try {
      setElegida((await fn()).id);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : generico);
    }
  }

  /*
    ── Las acciones viven DENTRO de la ficha (revisión del 2026-09-07) ──
    Eran una fila propia, con su margen, y encima repetían el enlace a
    «Cuestionarios» que las migas del shell ya ofrecen dos líneas más arriba.
    Entre esa fila y la cabecera vieja se iban 222 px antes de que empezara el
    trabajo, y el que los pagaba era el teléfono.

    El `BotonVolver` se quitó de esta pantalla: las migas llevan al mismo sitio y
    el menú lateral también. Tres caminos idénticos a un palmo de distancia no
    son tres comodidades.
  */
  const botonesDeVersion = !puedeArmar ? null : esBorrador ? (
    <>
      {c.versionPublicada !== null && (
        <Boton
          variante="secundario"
          className="px-4 py-2 text-sm"
          cargando={acciones.descartar.isPending}
          onClick={() => {
            if (!confirm('Se pierde todo lo que lleva este borrador. ¿Descartarlo?')) return;
            setElegida(null);
            void ejecutar(
              () => acciones.descartar.mutateAsync(),
              'No pudimos descartar el borrador.',
            );
          }}
        >
          {/* «Descartar» a secas: la fila son cuatro verbos y el modal de
              confirmación ya dice qué se pierde. */}
          Descartar
        </Boton>
      )}
      <Boton
        className="px-4 py-2 text-sm"
        cargando={acciones.publicar.isPending}
        onClick={() =>
          void ejecutarYMirar(
            () => acciones.publicar.mutateAsync(),
            'No pudimos publicar el cuestionario.',
          )
        }
      >
        Publicar
      </Boton>
    </>
  ) : (
    <Boton
      className="px-4 py-2 text-sm"
      cargando={acciones.abrir.isPending}
      onClick={() =>
        void ejecutarYMirar(() => acciones.abrir.mutateAsync(), 'No pudimos abrir el borrador.')
      }
    >
      {c.tieneBorrador
        ? 'Seguir editando el borrador'
        : `Editar — crea la versión ${Math.max(...lista.map((x) => x.version)) + 1}`}
    </Boton>
  );

  return (
    /*
      ── Un solo scroll, y lo reparte flexbox (revisión del 2026-09-07) ──
      Antes había TRES barras: la del `<main>` del shell, la de la columna de
      trabajo y la del teléfono. El intento anterior fijaba el alto con
      `calc(100dvh - 13rem)`, un número adivinado que no coincidía con el
      encabezado real, así que el contenido desbordaba el `main` y este sacaba su
      propia barra.

      Ahora la página mide `h-full` —el `main` ya tiene altura definida, es
      `flex-1` dentro de un `h-dvh`— y reparte ese alto: encabezado fijo y el
      resto para el grid. Con el hijo midiendo exactamente lo que hay, el `main`
      no tiene nada que desplazar.
    */
    <Contenedor ancho="completo" className="flex h-full flex-col px-1">
      {/*
        ── Una sola ficha, y plegada (revisión del 2026-09-07) ──
        Antes esto eran DOS bloques apilados —la cabecera con el título, la
        descripción y el selector de versión, y debajo una tarjeta solo para la
        portada— más de 200 px de alto que empujaban la construcción hacia abajo
        y dejaban el teléfono encogido. Y el título y la descripción **no se
        podían editar desde acá**: había que volver al listado.

        Ahora es una ficha: cerrada muestra lo que hace falta saber de un vistazo
        —nombre, estado, qué versión se mira— y abierta deja cambiar nombre,
        descripción y portada sin salir de la pantalla. Cerrada por omisión,
        porque a esta pantalla se entra a armar preguntas, no a renombrar.
      */}
      <FichaCuestionario
        cuestionario={c}
        estadoVersion={v.estado}
        versiones={lista}
        versionId={versionId}
        alVerVersion={setElegida}
        puedeEditar={puedeArmar}
        acciones={botonesDeVersion}
        revisionPortada={revisionPortada}
        alSubirPortada={async (archivo) => {
          await portada.subir.mutateAsync(archivo);
          setRevisionPortada((n) => n + 1);
        }}
        alQuitarPortada={async () => {
          await portada.quitar.mutateAsync();
        }}
      />

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {!esBorrador && (
        <p className="mb-5 rounded-xl border border-marino-100 bg-marino-50 px-4 py-3 text-sm text-marino-600">
          {v.estado === 'publicada'
            ? 'Esta versión no se edita: puede tener respuestas, y cambiarla haría que el «antes» y el «después» dejaran de medir lo mismo. Para cambiar algo, «Editar» crea una versión nueva.'
            : 'Esta versión se retiró: ya no se ofrece para aplicaciones nuevas, pero se conserva porque las respuestas que se dieron apuntan a ella.'}
        </p>
      )}

      {/*
        La herramienta toma el espacio flexible; el teléfono, su ancho natural.
        En pantallas angostas se apilan: primero la herramienta —lo primero que
        se hace es escribir— y el teléfono debajo.
      */}
      {/*
        ── Cada columna con su propio scroll, no la página entera ──
        Con veinte preguntas la columna de trabajo se vuelve altísima y el scroll
        de la página se lleva el teléfono fuera de vista. El `sticky` no lo
        resolvía: el teléfono mide 44 rem, así que en un viewport más corto no
        cabe entero y pegarlo arriba solo recorta la parte de abajo.

        Ahora el bloque se acota al alto de la ventana y **cada lado se desplaza
        solo**: la lista de preguntas baja y el teléfono se queda donde está. Es
        lo que permite ver el efecto de lo que se edita sin perder de vista
        ninguno de los dos.

        Solo desde `lg`: por debajo las columnas se apilan y el scroll normal de
        la página es lo correcto — encerrar una lista larga en una caja de 300 px
        dentro de una pantalla que ya hace scroll es peor que no hacer nada.
      */}
      <div
        /*
          ⚠️ La columna del teléfono mide **26rem fijos** (416 px = 390 de
          pantalla + 26 de marco), no `auto`. Con `auto` el ancho salía del
          contenido, y el contenido ahora se escala con un `transform`: el
          ResizeObserver que calcula la escala mide su hueco, el hueco dependería
          del contenido, y el contenido de la escala. Ese lazo se resuelve
          fijando el ancho de la columna.
        */
        className="grid min-h-0 flex-1 gap-6 lg:grid-cols-[minmax(0,1fr)_26rem]
          lg:items-stretch lg:overflow-hidden"
      >
        <div className="min-w-0 lg:h-full lg:overflow-y-auto lg:pr-2">
          {esBorrador && puedeArmar ? (
            /*
              ⚠️ **El selector va ANTES del editor en esta cadena.**
              «Cambiar tipo» deja la edición en curso viva y solo enciende
              `eligiendoTipo`; si el editor tuviera prioridad, el botón no
              mostraría nada y parecería roto. Con el selector primero, se
              muestra encima de la edición y al elegir —o al cancelar— se vuelve
              exactamente a donde estaba.
            */
            eligiendoTipo ? (
              <ElegirTipo
                alElegir={(tipo) => {
                  setEligiendoTipo(false);
                  /*
                    Si había una edición en curso, se le cambia el tipo y se
                    conserva TODO lo demás. Empezar de cero borraría el texto ya
                    escrito, que es lo que menos depende del tipo.
                  */
                  /*
                    Una barra nueva arranca con su recorrido puesto. Sin rango no
                    se puede dibujar, así que dejarlo vacío haría que el tipo
                    naciera roto y obligara a adivinar qué números poner.
                  */
                  const conRango: Borrador =
                    tipo === 'barra'
                      ? {
                          ...VACIA,
                          tipo,
                          minimo: String(BARRA_MINIMO),
                          maximo: String(BARRA_MAXIMO),
                        }
                      : { ...VACIA, tipo };
                  setEdicion((previa) =>
                    previa
                      ? {
                          ...previa,
                          f: {
                            ...previa.f,
                            tipo,
                            ...(tipo === 'barra' && previa.f.minimo.trim() === ''
                              ? { minimo: String(BARRA_MINIMO), maximo: String(BARRA_MAXIMO) }
                              : {}),
                          },
                        }
                      : { preguntaId: null, f: conRango, original: conRango },
                  );
                }}
                alCancelar={() => setEligiendoTipo(false)}
                hayEdicionEnCurso={edicion !== null}
              />
            ) : edicion ? (
              <EditorPregunta
                cuestionarioId={id}
                preguntaId={edicion.preguntaId}
                f={edicion.f}
                escalas={escalas.data ?? []}
                alCambiar={(f) => setEdicion({ ...edicion, f })}
                alCerrar={() => dejarEdicion()}
                hayCambios={hayCambios}
                tieneImagen={
                  v.preguntas.find((p) => p.id === edicion.preguntaId)?.tieneImagen ?? false
                }
                /*
                  Volver a elegir tipo NO descarta lo escrito: el texto, la
                  ayuda y la competencia sirven igual con otro tipo. Solo se
                  guarda que había una edición en curso para retomarla.

                  Por eso no pasa por `dejarEdicion`: no se está abandonando
                  nada, se está cambiando una parte de lo que se edita.
                */
                alCambiarTipo={() => setEligiendoTipo(true)}
              />
            ) : (
              <PanelPreguntas
                version={v}
                alAgregar={(tipo) => {
                  /*
                    Una sección va directa: es un solo tipo y su editor no tiene
                    alternativa que elegir. Una pregunta pasa por el selector.
                  */
                  if (tipo === 'seccion') {
                    dejarEdicion({
                      preguntaId: null,
                      f: { ...VACIA, tipo },
                      original: { ...VACIA, tipo },
                    });
                    return;
                  }
                  if (dejarEdicion()) setEligiendoTipo(true);
                }}
                alEditar={(p) =>
                  dejarEdicion({ preguntaId: p.id, f: aBorrador(p), original: aBorrador(p) })
                }
                alDuplicar={(p) => {
                  const copia = { ...aBorrador(p), texto: `${p.texto} (copia)` };
                  /*
                    `original` es la copia MISMA, no la pregunta de la que sale:
                    duplicar y no tocar nada más no es «un cambio sin guardar»,
                    es una copia lista para guardar. Con el original apuntando a
                    la pregunta de origen, cerrar preguntaría siempre.
                  */
                  dejarEdicion({ preguntaId: null, f: copia, original: copia });
                }}
                alQuitar={(preguntaId) =>
                  confirm('¿Quitar esto del borrador?') &&
                  void ejecutar(
                    () => acciones.quitar.mutateAsync(preguntaId),
                    'No pudimos quitarla.',
                  )
                }
                alMover={(desde, hasta) => {
                  const ids = v.preguntas.map((p) => p.id);
                  const [movida] = ids.splice(desde, 1);
                  if (movida === undefined) return;
                  ids.splice(hasta, 0, movida);
                  void ejecutar(
                    () => acciones.reordenar.mutateAsync(ids),
                    'No pudimos cambiar el orden.',
                  );
                }}
              />
            )
          ) : (
            <div className="rounded-2xl border border-marino-100 bg-white p-5">
              <h2 className="font-semibold text-marino-800">
                {v.preguntas.filter((p) => !esSeccion(p.tipo)).length} preguntas
              </h2>
              <p className="mt-1 text-sm text-marino-400">
                Esta versión no se edita. El teléfono la muestra tal como la ve quien responde —
                puedes recorrerla con «Comenzar» y «Siguiente».
              </p>
            </div>
          )}
        </div>

        {/*
          ⚠️ `overflow-hidden`, NO `overflow-y-auto`. El teléfono se adapta al
          alto disponible en vez de desbordar, así que no hay nada que
          desplazar acá — y una barra de scroll alrededor de un teléfono
          dibujado es justo lo que hacía que la previa se viera desajustada del
          panel de al lado.
        */}
        <section aria-label="Vista previa" className="flex min-h-0 flex-col lg:overflow-hidden">
          <h2 className="mb-2 text-center text-xs font-semibold uppercase tracking-widest text-marino-400">
            Así se responde en el teléfono
          </h2>
          <VistaFormulario
            cuestionarioId={id}
            tienePortada={c.tienePortada}
            nombre={c.nombre}
            descripcion={c.descripcion}
            preguntas={preguntasEnPantalla}
            escalas={escalas.data ?? []}
            idResaltado={idResaltado}
          />
        </section>
      </div>
    </Contenedor>
  );
}

// ---------------------------------------------------------------------------
// Panel izquierdo — la lista
// ---------------------------------------------------------------------------

/**
 * ── La lista, agrupada por páginas (revisión del 2026-09-07) ──
 *
 * Antes era una lista plana con las secciones intercaladas y separadas por un
 * margen. Con cuatro secciones y quince preguntas, **la estructura que el
 * trabajador va a recorrer no se leía**: había que reconstruir mentalmente
 * dónde empieza cada página contando desde cada sección hacia abajo.
 *
 * Ahora cada sección abre un grupo con su color, su título y **cuántas preguntas
 * tiene**, y las preguntas van indentadas debajo. Se ve de un vistazo si una
 * página quedó con nueve preguntas y la siguiente con una.
 *
 * ⚠️ **Solo se agrupa si hay secciones.** Sin ellas, un único grupo titulado
 * «Página 1» es una caja alrededor de todo que no informa de nada — y esta
 * pantalla ya tiene bastante que mirar.
 *
 * ⚠️ **Los índices que se le pasan a `alMover` son los del array PLANO**, no la
 * posición dentro del grupo. Reordenar sigue operando sobre la lista real; el
 * agrupamiento es solo cómo se dibuja. Confundirlos movería una pregunta a un
 * sitio distinto del que se ve, que es la peor clase de error en un reordenar.
 */
/**
 * La ficha del cuestionario: lo que NO es una pregunta.
 *
 * Nombre, descripción, portada, estado y qué versión se está mirando, todo en un
 * bloque que se pliega. Antes estaban repartidos en dos tarjetas y la mitad ni
 * se podía editar desde acá.
 *
 * ⚠️ **Nada de esto es del borrador.** El nombre, la descripción y la portada
 * cuelgan del cuestionario, no de la versión (D-125): cambiarlos no abre un
 * borrador, no publica y no toca ninguna pregunta. Por eso la ficha es un bloque
 * aparte y no una fila más de la lista — mezclarlas haría creer que se publican
 * juntas.
 */
function FichaCuestionario({
  cuestionario: c,
  estadoVersion,
  versiones,
  versionId,
  alVerVersion,
  puedeEditar,
  acciones,
  revisionPortada,
  alSubirPortada,
  alQuitarPortada,
}: {
  cuestionario: Cuestionario;
  estadoVersion: Version['estado'];
  versiones: ResumenVersion[];
  versionId: number | null;
  alVerVersion: (id: number) => void;
  puedeEditar: boolean;
  /** Publicar, descartar o abrir el borrador: lo que se hace con la versión. */
  acciones: React.ReactNode;
  revisionPortada: number;
  alSubirPortada: (f: File) => Promise<void>;
  alQuitarPortada: () => Promise<void>;
}) {
  const [abierta, setAbierta] = useState(false);
  const [versionesAbiertas, setVersionesAbiertas] = useState(false);
  const [nombre, setNombre] = useState(c.nombre);
  const [descripcion, setDescripcion] = useState(c.descripcion ?? '');
  const [fallo, setFallo] = useState<string | null>(null);
  const editar = useEditarCuestionario(c.id);

  /*
    ⚠️ **Los campos NO se resincronizan solos con lo que llega del servidor.**
    Subir una portada devuelve la fila entera del cuestionario, y un efecto que
    copiara ese nombre al campo le pisaría a quien esté escribiendo justo en ese
    momento lo que lleva tecleado. El estado local manda mientras la ficha esté
    abierta; el `key` de React la remonta si se cambia de cuestionario.
  */
  const cambiado = nombre !== c.nombre || descripcion !== (c.descripcion ?? '');

  async function guardar() {
    setFallo(null);
    try {
      await editar.mutateAsync({
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || null,
      });
    } catch (e) {
      setFallo(e instanceof ErrorApi ? e.message : 'No pudimos guardar los cambios.');
    }
  }

  const insignia =
    estadoVersion === 'borrador'
      ? { clase: 'bg-naranja-100 text-marino-800', texto: 'Borrador — todavía no lo ve nadie' }
      : estadoVersion === 'publicada'
        ? { clase: 'bg-marino-700 text-white', texto: 'Publicado — es lo que se responde' }
        : { clase: 'bg-marino-100 text-marino-500', texto: 'Versión retirada — solo consulta' };

  return (
    <section className="mb-4 rounded-3xl border border-marino-100 bg-white">
      {/* Lo que se ve siempre. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5">
        {/*
          La miniatura de la portada acompaña al título aunque la ficha esté
          cerrada: es la forma más corta de saber si el cuestionario tiene
          carátula sin abrir nada.
        */}
        {c.tienePortada && (
          <ImagenCuestionario
            ruta={`/api/cuestionarios/${c.id}/portada`}
            hay
            alt=""
            version={revisionPortada}
            className="h-9 w-14 shrink-0 rounded-xl"
          />
        )}

        <h1 className="min-w-0 truncate text-xl font-bold text-marino-800">{c.nombre}</h1>

        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${insignia.clase}`}>
          {insignia.texto}
        </span>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {/*
            El historial de versiones. Solo cuando hay más de una: con una sola
            no hay nada entre lo que saltar, y un control sin elección es ruido.
          */}
          {/*
            ── Versión: un botón que abre un modal, no un desplegable ──
            Fue un `<select>` con el texto entero dentro —un óvalo larguísimo— y
            después uno corto, «v2 · borrador». Las dos versiones tenían el mismo
            techo: **en una lista de opciones no cabe nada más que el nombre**, y
            de una versión hay que saber cuándo se publicó, cuántas preguntas
            lleva y en cuántas páginas. Eso es una tarjeta, no una opción.

            Los cuatro botones dicen lo que hacen y nada más: Versión, Editar,
            Descartar, Publicar.
          */}
          {versiones.length > 1 && (
            <Boton
              variante="secundario"
              className="px-4 py-2 text-sm"
              onClick={() => setVersionesAbiertas(true)}
            >
              <Reloj size={15} />
              Versión
            </Boton>
          )}

          {puedeEditar && (
            <Boton
              variante="secundario"
              className="px-4 py-2 text-sm"
              onClick={() => setAbierta((x) => !x)}
              aria-expanded={abierta}
              title="Nombre, descripción y portada"
            >
              {abierta ? <Check size={15} /> : <Lapiz size={15} />}
              {abierta ? 'Listo' : 'Editar'}
            </Boton>
          )}

          {acciones}
        </div>
      </div>

      {/* La descripción, cuando la ficha está cerrada y hay una. */}
      {!abierta && c.descripcion && (
        <p className="truncate px-5 pb-3.5 text-sm text-marino-400">{c.descripcion}</p>
      )}

      <ModalVersiones
        abierto={versionesAbiertas}
        versiones={versiones}
        versionId={versionId}
        alElegir={(id) => {
          alVerVersion(id);
          setVersionesAbiertas(false);
        }}
        alCerrar={() => setVersionesAbiertas(false)}
      />

      {abierta && puedeEditar && (
        <div className="grid gap-5 border-t border-marino-100 px-5 py-4 md:grid-cols-[18rem_minmax(0,1fr)]">
          <CampoImagen
            titulo="Portada del cuestionario"
            ayuda="Sin portada. Es lo primero que ve quien va a responder."
            hay={c.tienePortada}
            vistaPrevia={
              <ImagenCuestionario
                ruta={`/api/cuestionarios/${c.id}/portada`}
                hay={c.tienePortada}
                alt=""
                version={revisionPortada}
                className="h-full w-full"
              />
            }
            alSubir={alSubirPortada}
            alQuitar={alQuitarPortada}
          />

          <div className="space-y-3">
            <Campo
              etiqueta="Nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
            <Campo
              etiqueta="Descripción (opcional)"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />

            <div className="flex flex-wrap items-center gap-2">
              <Boton
                className="px-4 py-2 text-sm"
                disabled={!cambiado || nombre.trim().length < 3}
                cargando={editar.isPending}
                onClick={() => void guardar()}
              >
                Guardar
              </Boton>
              {cambiado && (
                <button
                  type="button"
                  onClick={() => {
                    setNombre(c.nombre);
                    setDescripcion(c.descripcion ?? '');
                    setFallo(null);
                  }}
                  className="rounded-full px-3.5 py-2 text-sm font-semibold text-marino-500
                    transition hover:text-marino-800 hover:underline"
                >
                  Deshacer
                </button>
              )}
              {/*
                Se dice que hay algo sin guardar. La portada se guarda sola al
                elegirla y el nombre no: sin este aviso, quien cambie las dos
                cosas a la vez se va creyendo que guardó las dos.
              */}
              {cambiado && (
                <span className="text-xs font-medium text-naranja-600">Sin guardar</span>
              )}
            </div>

            {fallo && <p className="text-xs font-medium text-red-600">{fallo}</p>}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * El historial de versiones, como tarjetas.
 *
 * ── Por qué un modal y no una lista de opciones ──
 * De una versión hay tres cosas que hacen falta para elegir bien: **en qué
 * estado está**, **cuándo se publicó** y **qué tan grande es**. En un `<option>`
 * no cabe ninguna de las tres —solo el nombre— así que el desplegable obligaba a
 * abrir versiones al azar para descubrir cuál era cuál.
 *
 * ⚠️ **Retirada no significa borrada.** Una versión retirada conserva las
 * respuestas que apuntan a ella, y por eso se puede abrir y consultar. Que la
 * tarjeta lo diga evita el reflejo de intentar limpiarla.
 */
function ModalVersiones({
  abierto,
  versiones,
  versionId,
  alElegir,
  alCerrar,
}: {
  abierto: boolean;
  versiones: ResumenVersion[];
  versionId: number | null;
  alElegir: (id: number) => void;
  alCerrar: () => void;
}) {
  const fecha = (iso: string) =>
    new Date(iso).toLocaleDateString('es-VE', { day: 'numeric', month: 'long', year: 'numeric' });

  const RASGOS = {
    borrador: {
      insignia: 'bg-naranja-100 text-marino-800',
      titulo: 'Borrador en trabajo',
      dice: 'Todavía no lo ve nadie. Es donde se edita.',
    },
    publicada: {
      insignia: 'bg-marino-700 text-white',
      titulo: 'Publicada',
      dice: 'Es la que se responde hoy.',
    },
    retirada: {
      insignia: 'bg-marino-100 text-marino-500',
      titulo: 'Retirada',
      dice: 'Ya no se aplica, pero se conserva: hay respuestas que apuntan a ella.',
    },
  } as const;

  return (
    <Modal titulo="Versiones de este cuestionario" abierto={abierto} alCerrar={alCerrar}>
      <ul className="space-y-2.5">
        {versiones.map((ver) => {
          const r = RASGOS[ver.estado];
          const viendo = ver.id === versionId;
          return (
            <li key={ver.id}>
              <button
                type="button"
                onClick={() => alElegir(ver.id)}
                aria-current={viendo}
                className={`w-full rounded-2xl border p-4 text-left transition
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                  ${
                    viendo
                      ? 'border-naranja-400 bg-naranja-50/50'
                      : 'border-marino-100 hover:bg-marino-50/60'
                  }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-bold text-marino-800">Versión {ver.version}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.insignia}`}>
                    {r.titulo}
                  </span>
                  {/*
                    Se dice cuál se está mirando. Sin esta marca, abrir el modal
                    para comparar dos versiones deja sin saber de dónde se
                    partió.
                  */}
                  {viendo && (
                    <span className="ml-auto text-xs font-bold uppercase tracking-wide text-naranja-600">
                      Viendo
                    </span>
                  )}
                </div>

                <p className="mt-1.5 text-sm text-marino-500">{r.dice}</p>

                <p className="mt-2 text-xs font-semibold text-marino-400">
                  {ver.cuantasPreguntas}{' '}
                  {ver.cuantasPreguntas === 1 ? 'pregunta' : 'preguntas'} en {ver.cuantasPaginas}{' '}
                  {ver.cuantasPaginas === 1 ? 'página' : 'páginas'}
                  {' · '}
                  {ver.publicadaEn
                    ? `publicada el ${fecha(ver.publicadaEn)}`
                    : `creada el ${fecha(ver.creadoEn)}`}
                </p>
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

function PanelPreguntas({
  version,
  alAgregar,
  alEditar,
  alDuplicar,
  alQuitar,
  alMover,
}: {
  version: Version;
  alAgregar: (tipo: TipoPregunta) => void;
  alEditar: (p: Pregunta) => void;
  alDuplicar: (p: Pregunta) => void;
  alQuitar: (id: number) => void;
  alMover: (desde: number, hasta: number) => void;
}) {
  const total = version.preguntas.filter((p) => !esSeccion(p.tipo)).length;
  const ultimo = version.preguntas.length - 1;

  /*
    Los grupos, conservando el índice global de cada fila. Las preguntas que van
    ANTES de la primera sección forman un grupo sin sección: existen de verdad
    —se responden en la primera página— y esconderlas por no tener cabecera
    dejaría preguntas invisibles en la lista.
  */
  type Fila = { p: Pregunta; i: number; numero: number };
  const grupos: { seccion: Fila | null; preguntas: Fila[] }[] = [];
  let numero = 0;
  for (const [i, p] of version.preguntas.entries()) {
    if (esSeccion(p.tipo)) {
      grupos.push({ seccion: { p, i, numero: 0 }, preguntas: [] });
      continue;
    }
    numero += 1;
    if (grupos.length === 0) grupos.push({ seccion: null, preguntas: [] });
    grupos[grupos.length - 1]!.preguntas.push({ p, i, numero });
  }

  const haySecciones = version.preguntas.some((p) => esSeccion(p.tipo));

  /*
    ── Páginas plegables ──
    Un cuestionario de cuatro secciones y veinte preguntas no cabe de un vistazo
    ni con scroll propio: para mover algo del final al principio hay que recorrer
    la lista entera. Plegando las páginas que no se están tocando, la estructura
    completa vuelve a caber en pantalla.

    ⚠️ El estado vive por ID de sección y NO por posición: reordenar cambia las
    posiciones, y con índices se plegaría la página equivocada al mover una.

    Arrancan todas abiertas. Plegado por omisión escondería el trabajo al entrar,
    y quien abre el constructor viene a ver lo que hay.
  */
  /*
    ── Reordenar: se arrastra la tarjeta ──
    Con `draggable` nativo, sin librería. Las alternativas —dnd-kit, react-beautiful-
    dnd— traen su propio modelo de estado y unos 30 KB para una lista que ya sabe
    moverse con `alMover(desde, hasta)`.

    Las flechas ↑↓ se quitaron: eran dos botones más en cada fila de una lista que
    ya tenía cuatro, y arrastrar es el gesto que la gente busca acá.

    ⚠️ **Pero el teclado no se pierde con ellas.** El arrastre nativo de HTML no
    responde a teclado ni a lector de pantalla, así que el asa `⠿` NO es un adorno:
    es un `<button>` de verdad, se alcanza con Tab y **mueve la fila con ↑ y ↓**.
    Es el patrón accesible de arrastre —asa enfocable con teclas—, no una versión
    disminuida. Si el asa vuelve a ser un `<span>`, reordenar deja de existir para
    quien no usa ratón.

    `arrastrando` guarda el ÍNDICE GLOBAL, el mismo que recibe `alMover`: el
    agrupamiento por páginas es solo cómo se dibuja, y mezclar la posición dentro
    del grupo con la real movería la pregunta a un sitio distinto del que se ve.
  */
  const [arrastrando, setArrastrando] = useState<number | null>(null);
  const [encima, setEncima] = useState<number | null>(null);

  /*
    Lo que hace que una fila —pregunta o cabecera de sección— se pueda arrastrar y
    soltar. Va en una función porque la cabecera de una página es un `div` y la
    pregunta un `li`, pero el gesto es el mismo.
  */
  const propsArrastre = (i: number) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      setArrastrando(i);
      e.dataTransfer.effectAllowed = 'move';
      // Firefox no inicia el arrastre si no se escribe algo en el `dataTransfer`.
      // El valor no se usa: lo que viaja es el índice del estado.
      e.dataTransfer.setData('text/plain', String(i));
    },
    onDragOver: (e: React.DragEvent) => {
      // Sin esto, `drop` no llega nunca: el navegador rechaza la zona por omisión.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move' as const;
      if (encima !== i) setEncima(i);
    },
    onDragEnd: () => {
      setArrastrando(null);
      setEncima(null);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      if (arrastrando !== null && arrastrando !== i) alMover(arrastrando, i);
      setArrastrando(null);
      setEncima(null);
    },
  });

  /** Cómo se ve la fila mientras se arrastra algo. */
  const claseArrastre = (i: number) =>
    `${arrastrando === i ? 'opacity-40' : ''} ` +
    `${encima === i && arrastrando !== null && arrastrando !== i ? 'ring-2 ring-naranja-400' : ''}`;

  /*
    El asa. Es un botón, no un icono decorativo: con Tab se llega y con ↑ y ↓ se
    mueve la fila. El `aria-label` dice qué se está moviendo y de dónde a dónde va
    a ir, porque un lector de pantalla que solo anuncia «mover» deja a quien
    escucha sin saber qué agarró.
  */
  const asa = (f: Fila, que: string, claro = false) => (
    <button
      type="button"
      {...propsArrastre(f.i)}
      aria-label={`Mover ${que}. Posición ${f.i + 1} de ${ultimo + 1}. Arrástralo, o usa las flechas arriba y abajo.`}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' && f.i > 0) {
          e.preventDefault();
          alMover(f.i, f.i - 1);
        }
        if (e.key === 'ArrowDown' && f.i < ultimo) {
          e.preventDefault();
          alMover(f.i, f.i + 1);
        }
      }}
      className={`relative shrink-0 cursor-grab rounded-full px-2 py-2 transition
        active:cursor-grabbing
        focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
        ${
          claro
            ? 'text-white/80 hover:bg-white/25 hover:text-white'
            : 'text-marino-300 hover:bg-white hover:text-marino-500'
        }`}
      title="Arrastra para mover, o usa ↑ y ↓"
    >
      <span aria-hidden="true">⠿</span>
    </button>
  );

  const [plegadas, setPlegadas] = useState<Set<number>>(new Set());
  const alternar = (idSeccion: number) =>
    setPlegadas((antes) => {
      const ahora = new Set(antes);
      if (!ahora.delete(idSeccion)) ahora.add(idSeccion);
      return ahora;
    });

  const controles = (f: Fila, claro = false) => (
    <div className="flex shrink-0 items-center">
      {/* Sin flechas: se reordena arrastrando, y con teclado desde el asa. */}
      <BotonIcono rotulo="Duplicar" claro={claro} onClick={() => alDuplicar(f.p)}>
        <Copiar size={14} />
      </BotonIcono>
      <BotonIcono rotulo="Quitar" claro={claro} onClick={() => alQuitar(f.p.id)}>
        <Equis size={14} />
      </BotonIcono>
    </div>
  );

  return (
    <div className="rounded-2xl border border-marino-100 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold text-marino-800">
          {total} {total === 1 ? 'pregunta' : 'preguntas'}
          {haySecciones && (
            <span className="ml-1.5 text-sm font-normal text-marino-400">
              en {grupos.length} {grupos.length === 1 ? 'página' : 'páginas'}
            </span>
          )}
        </h2>
        <div className="flex gap-2">
          {/* Dos altas distintas porque son dos gestos distintos: una pregunta
              se agrega a cada rato; una sección, unas pocas veces. */}
          <Boton
            variante="secundario"
            className="px-3 py-1.5 text-sm"
            onClick={() => alAgregar('seccion')}
          >
            Nueva sección
          </Boton>
          <Boton className="px-3 py-1.5 text-sm" onClick={() => alAgregar('likert')}>
            Agregar pregunta
          </Boton>
        </div>
      </div>

      {version.preguntas.length === 0 ? (
        <p className="rounded-xl border border-dashed border-marino-200 px-3 py-6 text-center text-sm text-marino-400">
          Agrega la primera pregunta. Las secciones parten el cuestionario en páginas, cada una con
          su título y su color.
        </p>
      ) : (
        <div className="space-y-3">
          {grupos.map((g, iGrupo) => {
            const seccion = g.seccion;
            const acento = seccion ? ACENTOS[seccion.p.color ?? 'marino'] : undefined;
            // Un grupo sin sección no se puede plegar: no tiene cabecera de la
            // que colgar el control, y esconderlo lo volvería inalcanzable.
            const plegada = seccion !== null && plegadas.has(seccion.p.id);
            return (
              /*
                ⚠️ `overflow-hidden` es lo que arregla la esquina.
                La cabecera llevaba su acento de color como `border-left` de 4 px,
                y un borde recto contra una esquina redondeada deja exactamente lo
                que se reportó: un trozo recto y después una curva. Con el grupo
                recortando a sus esquinas, la barra de color entra en la curva y
                el bloque queda simétrico por los cuatro lados.
              */
              <div
                key={seccion?.p.id ?? `sin-seccion-${iGrupo}`}
                className={
                  haySecciones ? 'overflow-hidden rounded-2xl border border-marino-100' : ''
                }
                /*
                  ⚠️ La barra de color va en el GRUPO y no en la cabecera. En la
                  cabecera medía lo que mide la cabecera, así que el borde
                  izquierdo del bloque era: curva, trozo recto de color, y otra
                  vez borde gris — la asimetría que se reportó. Recorriendo la
                  tarjeta entera, el bloque se lee como una sola pieza y las dos
                  esquinas de la izquierda curvan igual que las de la derecha.
                */
                style={
                  seccion && haySecciones
                    ? { borderLeft: `4px solid ${acento?.fuerte}` }
                    : undefined
                }
              >
                {/* La cabecera del grupo: la sección, si la hay. */}
                {seccion && (
                  /*
                    La cabecera también se arrastra: mover una página es mover su
                    sección, igual que hacían las flechas. ⚠️ Mueve **la sección
                    sola**, no sus preguntas — es la misma operación de siempre, y
                    en la lista plana la sección es una fila más.
                  */
                  <div
                    {...propsArrastre(seccion.i)}
                    className={`relative flex items-center gap-1 border-b border-marino-100 pr-1 transition
                      ${seccion.p.tieneImagen ? '' : 'bg-marino-50/60'}
                      ${claseArrastre(seccion.i)}`}
                  >
                    {/*
                      ── Con imagen, la cabecera ES la imagen ──
                      Antes la imagen iba de miniatura de 44 px dentro de la fila,
                      y eso no dice nada útil: a ese tamaño no se distingue una
                      foto de otra, y encima la fila seguía leyéndose como una
                      fila de texto cuando en el teléfono es una portada.

                      Ahora la cabecera crece a una banda y la imagen la ocupa
                      entera, con el título en blanco encima. Es la misma
                      composición que el trabajador va a ver, en pequeño — que es
                      lo que se le pide a un constructor.
                    */}
                    {seccion.p.tieneImagen && (
                      <>
                        <ImagenCuestionario
                          ruta={`/api/cuestionarios/preguntas/${seccion.p.id}/imagen`}
                          hay
                          alt=""
                          className="absolute inset-0 h-full w-full"
                        />
                        {/*
                          El degradado no es adorno: sin él, una foto clara deja
                          el título ilegible, y eso no se descubre hasta que
                          alguien sube una foto clara.
                        */}
                        <div className="absolute inset-0 bg-gradient-to-r from-marino-900/90 via-marino-900/75 to-marino-900/45" />
                      </>
                    )}
                    {asa(seccion, `la página ${iGrupo + 1}`, seccion.p.tieneImagen)}
                    {/*
                      ── El clic en la cabecera PLIEGA, no edita ──
                      Antes editaba, y el triángulo de al lado plegaba. Estaba al
                      revés de lo que la forma promete: una fila con un triángulo
                      delante se lee como un desplegable, así que quien quería
                      cerrar la página abría el editor sin buscarlo. Ahora toda la
                      cabecera es el desplegable —el triángulo incluido— y editar
                      es un botón propio al final, junto a duplicar y quitar.
                    */}
                    <button
                      type="button"
                      onClick={() => alternar(seccion.p.id)}
                      aria-expanded={!plegada}
                      className={`relative flex min-w-0 flex-1 items-center gap-2 rounded-2xl pr-3 text-left
                        focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                        ${seccion.p.tieneImagen ? 'py-6' : 'py-2'}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`shrink-0 transition-transform
                          ${seccion.p.tieneImagen ? 'text-white/80' : 'text-marino-400'}
                          ${plegada ? '-rotate-90' : ''}`}
                      >
                        ▾
                      </span>
                      <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
                        <span
                          className={`truncate text-sm font-bold uppercase tracking-wide
                            ${seccion.p.tieneImagen ? 'text-white' : 'text-marino-700'}`}
                        >
                          {seccion.p.texto || 'Sección sin título'}
                        </span>
                        <span
                          className={`shrink-0 text-xs font-semibold
                            ${seccion.p.tieneImagen ? 'text-white/75' : 'text-marino-400'}`}
                        >
                          Página {iGrupo + 1} · {g.preguntas.length}{' '}
                          {g.preguntas.length === 1 ? 'pregunta' : 'preguntas'}
                        </span>
                      </span>
                    </button>
                    <div
                      className={`relative flex shrink-0 items-center
                        ${seccion.p.tieneImagen ? 'rounded-full bg-marino-900/45 px-0.5' : ''}`}
                    >
                      <BotonIcono
                        rotulo="Editar sección"
                        claro={seccion.p.tieneImagen}
                        onClick={() => alEditar(seccion.p)}
                      >
                        <Lapiz size={14} />
                      </BotonIcono>
                      {controles(seccion, seccion.p.tieneImagen)}
                    </div>
                  </div>
                )}

                {/*
                  Una página sin preguntas se dice, no se deja en blanco: una
                  sección suelta al final del borrador es una página que el
                  trabajador vería vacía, y eso hay que poder verlo acá.
                */}
                {plegada ? null : g.preguntas.length === 0 ? (
                  /*
                    Un vacío que dice qué hacer, y no solo que está vacío. Las dos
                    salidas son las dos que existen de verdad: crear una pregunta
                    acá, o arrastrar una que ya está en otra página. Decir solo
                    «no tiene preguntas» deja a quien lo lee buscando el botón.
                  */
                  <div className="m-2 rounded-2xl border border-dashed border-marino-200 px-4 py-5 text-center">
                    <p className="text-sm font-semibold text-marino-600">
                      Esta página todavía no tiene preguntas
                    </p>
                    <p className="mt-1 text-xs text-marino-400">
                      Crea una con «Agregar pregunta», o arrastra hasta acá una de otra página.
                    </p>
                  </div>
                ) : (
                  <ol className={haySecciones ? 'p-1' : 'space-y-1'}>
                    {g.preguntas.map((f) => (
                      <li
                        key={f.p.id}
                        {...propsArrastre(f.i)}
                        className={`flex items-center gap-1 rounded-2xl transition
                          ${arrastrando === f.i ? '' : 'hover:bg-marino-50/70'}
                          ${claseArrastre(f.i)}`}
                      >
                        {asa(f, `la pregunta ${f.numero}`)}
                        <button
                          type="button"
                          onClick={() => alEditar(f.p)}
                          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-2xl px-2 py-2 text-left
                            focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                        >
                          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-marino-50 text-xs font-semibold text-marino-500">
                            {f.numero}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-marino-800">
                              {f.p.texto || 'Pregunta sin texto'}
                            </span>
                            <span className="block truncate text-xs text-marino-400">
                              {TIPO_PREGUNTA[f.p.tipo].titulo}
                              {f.p.competencia && ` · ${f.p.competencia}`}
                            </span>
                          </span>
                        </button>
                        {controles(f)}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BotonIcono({
  rotulo,
  desactivado,
  claro = false,
  onClick,
  children,
}: {
  rotulo: string;
  desactivado?: boolean;
  /** Sobre fondo oscuro —la cabecera de una sección con imagen— se invierte. */
  claro?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={rotulo}
      aria-label={rotulo}
      disabled={desactivado}
      onClick={onClick}
      /*
        `rounded-full` y no `rounded-lg`: en un cuadro de 28 px, un radio de 8 px
        deja una esquina apenas curva que al lado de las píldoras de la pantalla
        se lee como un rectángulo. Redondo y simétrico, como todo lo demás.
      */
      className={`flex h-8 w-8 items-center justify-center rounded-full transition
        disabled:cursor-not-allowed disabled:opacity-25
        focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
        ${
          claro
            ? 'text-white/85 hover:bg-white/25 hover:text-white'
            : 'text-marino-400 hover:bg-white hover:text-marino-800'
        }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Panel izquierdo — el editor
// ---------------------------------------------------------------------------

type Borrador = {
  tipo: TipoPregunta;
  texto: string;
  ayuda: string;
  obligatoria: boolean;
  competenciaId: number | null;
  escalaId: number | null;
  minimo: string;
  maximo: string;
  etiquetaMin: string;
  etiquetaMax: string;
  color: string | null;
  emoji: string;
  admiteObservacion: boolean;
  observacionRotulo: string;
  items: string[];
};

const VACIA: Borrador = {
  tipo: 'likert',
  texto: '',
  ayuda: '',
  obligatoria: true,
  competenciaId: null,
  escalaId: null,
  minimo: '',
  maximo: '',
  etiquetaMin: '',
  etiquetaMax: '',
  color: null,
  emoji: '',
  admiteObservacion: false,
  observacionRotulo: '',
  items: [],
};

const aBorrador = (p: Pregunta): Borrador => ({
  tipo: p.tipo,
  texto: p.texto,
  ayuda: p.ayuda ?? '',
  obligatoria: p.obligatoria,
  competenciaId: p.competenciaId,
  escalaId: p.escalaId,
  minimo: p.minimo === null ? '' : String(p.minimo),
  maximo: p.maximo === null ? '' : String(p.maximo),
  etiquetaMin: p.etiquetaMin ?? '',
  etiquetaMax: p.etiquetaMax ?? '',
  color: (p.color as ColorSeccion | null) ?? null,
  emoji: p.emoji ?? '',
  admiteObservacion: p.admiteObservacion,
  observacionRotulo: p.observacionRotulo ?? '',
  items: p.items.map((i) => i.texto),
});

/**
 * El borrador del formulario con forma de `Pregunta`, para que el teléfono lo
 * pinte mientras se teclea.
 *
 * ⚠️ Los ítems reciben ids **negativos**: React necesita una clave estable por
 * fila y los ítems en curso no tienen id de la base todavía. Positivos podrían
 * chocar con ids reales del mismo listado.
 */
function aPreguntaPrevia(
  f: Borrador,
  id: number,
  orden: number,
  tieneImagen = false,
): Pregunta {
  const num = (s: string) => (s.trim() === '' ? null : Number(s));
  return {
    id,
    orden,
    tipo: f.tipo,
    texto: f.texto,
    ayuda: f.ayuda.trim() || null,
    obligatoria: f.obligatoria,
    competenciaId: f.competenciaId,
    competencia: null,
    escalaId: usaEscala(f.tipo) ? f.escalaId : null,
    minimo: usaRango(f.tipo) ? num(f.minimo) : null,
    maximo: usaRango(f.tipo) ? num(f.maximo) : null,
    etiquetaMin: usaExtremos(f.tipo) ? (f.etiquetaMin.trim() || null) : null,
    etiquetaMax: usaExtremos(f.tipo) ? (f.etiquetaMax.trim() || null) : null,
    color: esSeccion(f.tipo) ? (f.color ?? 'marino') : null,
    emoji: f.emoji.trim() || null,
    /*
      La imagen NO sale del borrador de edicion: no es uno de los campos que se
      teclean, se sube aparte y al momento. Viene de la fila guardada, para que
      editarle el titulo a una seccion con foto no la haga desaparecer del
      telefono mientras se escribe.
    */
    tieneImagen,
    admiteObservacion: admiteObservacion(f.tipo) ? f.admiteObservacion : false,
    observacionRotulo: f.observacionRotulo.trim() || null,
    items: usaItems(f.tipo)
      ? f.items.map((texto, i) => ({ id: -(i + 1), orden: i + 1, texto }))
      : [],
  };
}

function EditorPregunta({
  cuestionarioId,
  preguntaId,
  f,
  escalas,
  alCambiar,
  alCerrar,
  hayCambios,
  alCambiarTipo,
  tieneImagen,
}: {
  cuestionarioId: number;
  preguntaId: number | null;
  f: Borrador;
  escalas: Escala[];
  alCambiar: (f: Borrador) => void;
  alCerrar: () => void;
  hayCambios: boolean;
  /**
   * Si esta sección ya tiene imagen guardada. Viene de la versión y NO del
   * borrador de edición: la imagen se sube aparte y al momento, así que no es
   * uno de los campos que se están editando ni cuenta como cambio sin guardar.
   */
  tieneImagen: boolean;
  /** Vuelve al paso de elegir tipo, conservando lo ya escrito. */
  alCambiarTipo: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});
  /*
    Cambia con cada subida para que la imagen se vuelva a pedir. Sin esto,
    reemplazar la imagen de una sección no se vería: `tieneImagen` sigue en
    `true`, el efecto no se vuelve a disparar y el object URL muestra la anterior
    hasta que alguien recargue la página.
  */
  const [revisionImagen, setRevisionImagen] = useState(0);

  const competencias = useCatalogo('competencia');
  const { crear: crearOpcion } = useGuardarOpcion();
  const acciones = useBorrador(cuestionarioId);
  const crearEscala = useCrearEscala();
  const editarEscala = useEditarEscala();
  const activarEscala = useActivarEscala();
  const sets = useSets();
  const [setsAbiertos, setSetsAbiertos] = useState(false);
  /** `{escala: null}` es «crear»; con escala, «editar esa». */
  const [escalaEnEdicion, setEscalaEnEdicion] = useState<{ escala: Escala | null } | null>(null);
  const escalaElegida = escalas.find((e) => e.id === f.escalaId) ?? null;

  const cambiar = <K extends keyof Borrador>(k: K, v: Borrador[K]) => alCambiar({ ...f, [k]: v });
  const seccion = esSeccion(f.tipo);

  // Escala por omisión cuando solo hay una activa: elegir de una lista de un
  // elemento es un paso que nunca aporta información.
  useEffect(() => {
    if (!usaEscala(f.tipo) || f.escalaId !== null) return;
    const activas = escalas.filter((e) => e.activo);
    const unica = activas.length === 1 ? activas[0] : undefined;
    if (unica) alCambiar({ ...f, escalaId: unica.id });
  }, [f.tipo, f.escalaId, escalas]);

  async function enviar() {
    setError(null);
    setCampos({});

    const num = (s: string) => (s.trim() === '' ? null : Number(s));
    const datos: EntradaGuardarPregunta = {
      tipo: f.tipo,
      texto: f.texto.trim(),
      ayuda: f.ayuda.trim() || null,
      obligatoria: seccion ? false : f.obligatoria,
      competenciaId: seccion ? null : f.competenciaId,
      escalaId: usaEscala(f.tipo) ? f.escalaId : null,
      minimo: usaRango(f.tipo) ? num(f.minimo) : null,
      maximo: usaRango(f.tipo) ? num(f.maximo) : null,
      etiquetaMin: usaExtremos(f.tipo) ? (f.etiquetaMin.trim() || null) : null,
      etiquetaMax: usaExtremos(f.tipo) ? (f.etiquetaMax.trim() || null) : null,
      color: seccion ? (f.color ?? 'marino') : null,
      /*
        Se manda tal cual venía. Ya no hay control que lo cambie, así que esto es
        lo que CONSERVA el emoji de una sección guardada hace tiempo: mandar
        `null` aquí lo borraría en silencio la primera vez que alguien le tocara
        el título.
      */
      emoji: f.emoji.trim() || null,
      admiteObservacion: admiteObservacion(f.tipo) ? f.admiteObservacion : false,
      observacionRotulo:
        admiteObservacion(f.tipo) && f.admiteObservacion
          ? (f.observacionRotulo.trim() || null)
          : null,
      items: usaItems(f.tipo) ? f.items.map((i) => i.trim()).filter(Boolean) : [],
    };

    try {
      if (preguntaId === null) await acciones.agregar.mutateAsync(datos);
      else await acciones.guardar.mutateAsync({ preguntaId, datos });
      alCerrar();
    } catch (e) {
      if (e instanceof ErrorApi) {
        setError(e.message);
        setCampos(e.campos ?? {});
        return;
      }
      setError('No pudimos guardar. Intenta de nuevo.');
    }
  }

  const rotulo =
    f.tipo === 'matriz_likert'
      ? { plural: 'Afirmaciones', singular: 'afirmación' }
      : { plural: 'Opciones', singular: 'opción' };

  return (
    <div className="rounded-2xl border border-marino-100 bg-white p-4">
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={alCerrar}
          className="flex h-8 w-8 items-center justify-center rounded-xl border border-marino-100
            text-marino-500 transition hover:border-marino-300 hover:text-marino-800
            focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
          aria-label="Volver a la lista"
          title="Volver a la lista"
        >
          <Flecha size={15} />
        </button>
        <h2 className="font-semibold text-marino-800">
          {preguntaId === null
            ? seccion
              ? 'Nueva sección'
              : 'Nueva pregunta'
            : seccion
              ? 'Editar sección'
              : 'Editar pregunta'}
        </h2>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/*
          ── El tipo, como INSIGNIA y no como selector (revisión del 2026-09-07) ──
          Acá vivía el `SelectorTipo` completo, y eso dejaba el mismo control en
          dos sitios: el paso previo de elegir tipo y otra vez dentro del editor.
          Diez tarjetas repetidas arriba de cada formulario son la mitad del
          scroll de esta pantalla, y encima invitan a dudar de si ya se eligió.

          Ahora el editor DECLARA el tipo y ofrece volver a elegirlo. Elegir y
          configurar quedan separados, que es lo que pidió el cliente.

          ⚠️ Y en una SECCIÓN no se muestra nada de esto. Una sección no es una
          pregunta: es el encabezado de las que vienen debajo, no se responde y no
          tiene tipo que elegir. Preguntarle «¿medir o elegir?» a algo que no se
          responde es una pregunta sin sentido — el cliente lo señaló y tenía
          razón.
        */}
        {!seccion && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-marino-100 bg-marino-50/60 px-3 py-2">
            <span className="flex items-center gap-2.5">
              <span className="w-14 shrink-0" aria-hidden="true">
                <Miniatura tipo={f.tipo} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-marino-800">
                  {TIPO_PREGUNTA[f.tipo].titulo}
                </span>
                <span className="block text-xs text-marino-500">
                  {TIPO_PREGUNTA[f.tipo].ayuda}
                </span>
              </span>
            </span>
            <Boton
              variante="secundario"
              className="px-2.5 py-1 text-xs"
              onClick={alCambiarTipo}
            >
              Cambiar tipo
            </Boton>
          </div>
        )}

        <Campo
          etiqueta={
            seccion ? 'Título de la sección' : f.tipo === 'matriz_likert' ? 'Enunciado de la matriz' : 'Pregunta'
          }
          value={f.texto}
          onChange={(e) => cambiar('texto', e.target.value)}
          error={campos.texto}
          autoFocus
        />

        <Campo
          etiqueta={seccion ? 'Subtítulo (opcional)' : 'Ayuda (opcional)'}
          value={f.ayuda}
          onChange={(e) => cambiar('ayuda', e.target.value)}
          error={campos.ayuda}
        />

        {/*
          ── El campo de emoji se quitó (revisión del 2026-09-07) ──
          Eran veinte botones y un campo de texto para decorar un título, y el
          teclado del sistema hace lo mismo mejor: Win + . en Windows, Ctrl + Cmd
          + Espacio en Mac, y el emoji se escribe DENTRO del enunciado o del
          subtítulo, donde uno quiera y cuantos quiera.

          ⚠️ **La columna `preguntas.emoji` NO se borró**, y lo que ya estaba
          guardado se sigue dibujando. Lo que no hay es forma de cambiarlo desde
          acá: guardar una pregunta lo deja como estaba. Quitar la columna es una
          migración y una decisión aparte —hay secciones publicadas que la usan—
          y no se hace de pasada.

          Que se pueden poner emojis en el texto va a la guía de usuario.
        */}

        {seccion && (
          <SelectorColor
            valor={f.color ?? 'marino'}
            alElegir={(color) => cambiar('color', color)}
          />
        )}

        {/*
          La imagen de la sección.

          ⚠️ **Solo cuando la sección ya está guardada.** Se sube contra el id de
          la pregunta, y mientras se está creando ese id todavía no existe: el
          único destino posible sería uno inventado, que es el camino corto a un
          archivo huérfano. Se dice con una frase en vez de mostrar un control que
          no puede funcionar — un botón que falla enseña menos que una explicación.
        */}
        {seccion &&
          (preguntaId === null ? (
            <p className="rounded-2xl border border-dashed border-marino-200 px-4 py-3 text-xs text-marino-400">
              Guarda la sección y vas a poder cargarle una imagen de cabecera.
            </p>
          ) : (
            <CampoImagen
              titulo="Imagen de la sección"
              ayuda="Una imagen de cabecera para esta página. Opcional."
              hay={tieneImagen}
              vistaPrevia={
                <ImagenCuestionario
                  ruta={`/api/cuestionarios/preguntas/${preguntaId}/imagen`}
                  hay={tieneImagen}
                  alt=""
                  version={revisionImagen}
                  className="h-full w-full"
                />
              }
              alSubir={async (archivo) => {
                await acciones.subirImagen.mutateAsync({ preguntaId, archivo });
                setRevisionImagen((n) => n + 1);
              }}
              alQuitar={async () => {
                await acciones.quitarImagen.mutateAsync(preguntaId);
              }}
            />
          ))}

        {!seccion && (
          <Combo
            etiqueta="Competencia que mide"
            opciones={(competencias.data ?? []).map((c) => ({ clave: c.id, texto: c.nombre }))}
            valor={f.competenciaId}
            alCambiar={(v) => cambiar('competenciaId', v === null ? null : Number(v))}
            alCrear={async (texto) => {
              const creada = await crearOpcion.mutateAsync({ tipo: 'competencia', nombre: texto });
              cambiar('competenciaId', creada.id);
              return { clave: creada.id, texto: creada.nombre };
            }}
            crearRotulo="Agregar competencia"
            vacio="Sin competencia"
            error={campos.competenciaId}
          />
        )}

        {usaEscala(f.tipo) && (
          <div>
            <Combo
              etiqueta="Escala"
              opciones={escalas
                .filter((e) => e.activo)
                .map((e) => ({
                  clave: e.id,
                  texto: e.nombre,
                  detalle: `${e.puntos.length} puntos`,
                }))}
              valor={f.escalaId}
              alCambiar={(v) => cambiar('escalaId', v === null ? null : Number(v))}
              vacio="Elige una escala"
              error={campos.escalaId}
            />

            {/*
              ── Crear y editar la escala DESDE acá ──
              Hasta hoy no había ninguna pantalla para esto (D-134): las dos
              escalas del sistema entraron por migración y el constructor solo
              dejaba elegir entre ellas. El sitio natural es este, porque es
              donde uno se da cuenta de que la escala que necesita no existe o
              que un punto está mal escrito.
            */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setEscalaEnEdicion({ escala: null })}
                className="rounded-full border border-marino-200 bg-white px-3.5 py-1.5 text-sm
                  font-semibold text-marino-700 transition hover:bg-marino-50
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
              >
                Nueva escala
              </button>
              {escalaElegida && (
                <button
                  type="button"
                  onClick={() => setEscalaEnEdicion({ escala: escalaElegida })}
                  className="rounded-full px-3.5 py-1.5 text-sm font-semibold text-marino-500
                    transition hover:text-marino-800 hover:underline
                    focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                >
                  {escalaElegida.usosPublicados > 0
                    ? 'Ver los puntos de esta escala'
                    : 'Editar los puntos de esta escala'}
                </button>
              )}
            </div>

            {/*
              Lo que el trabajador va a leer, escrito acá. ⚠️ Se muestra la
              etiqueta de CADA punto, no solo los extremos: desde D-131 el
              significado aparece al elegir, así que un punto sin rótulo se ve —y
              acá es donde hay que notarlo, no en el teléfono.
            */}
            {escalaElegida && (
              <ul className="mt-3 space-y-1 rounded-2xl border border-marino-100 bg-marino-50/50 px-4 py-3">
                {escalaElegida.puntos.map((p) => (
                  <li key={p.id} className="flex items-baseline gap-2 text-xs">
                    <span className="w-4 shrink-0 font-bold text-marino-400">{p.orden}</span>
                    {p.emoji && <span aria-hidden="true">{p.emoji}</span>}
                    <span
                      className={
                        p.etiqueta.trim() ? 'text-marino-600' : 'font-semibold text-red-600'
                      }
                    >
                      {p.etiqueta.trim() || 'Sin significado — el trabajador no sabrá qué elige'}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <ModalEscala
              abierto={escalaEnEdicion !== null}
              escala={escalaEnEdicion?.escala ?? null}
              sets={sets.lista.data ?? []}
              alAdministrarSets={() => setSetsAbiertos(true)}
              alCerrar={() => setEscalaEnEdicion(null)}
              alGuardar={async (datos) => {
                const previa = escalaEnEdicion?.escala;
                if (previa) {
                  await editarEscala.mutateAsync({ escalaId: previa.id, datos });
                } else {
                  const creada = await crearEscala.mutateAsync(datos);
                  // Recién creada, se elige: nadie crea una escala para no usarla.
                  cambiar('escalaId', creada.id);
                }
                setEscalaEnEdicion(null);
              }}
              alDesactivar={
                escalaEnEdicion?.escala
                  ? async () => {
                      const id = escalaEnEdicion.escala!.id;
                      await activarEscala.desactivar.mutateAsync(id);
                      // Si era la elegida, se suelta: quedaría apuntando a una
                      // escala que ya no aparece en la lista.
                      if (f.escalaId === id) cambiar('escalaId', null);
                      setEscalaEnEdicion(null);
                    }
                  : undefined
              }
            />

            {/*
              La pantalla de sets. Vive acá y no en Configuración porque es donde
              se descubre que hace falta: al elegir con qué se dibuja la escala.
              Se abre ENCIMA del modal de la escala y al cerrarla se vuelve a lo
              que se estaba haciendo, con el set nuevo ya en la lista.
            */}
            <ModalSets
              abierto={setsAbiertos}
              sets={sets.lista.data ?? []}
              alCerrar={() => setSetsAbiertos(false)}
              alCrear={async (nombre) => {
                await sets.crear.mutateAsync(nombre);
              }}
              alSubir={async (setId, archivo) => {
                await sets.subir.mutateAsync({ setId, archivo });
              }}
              alQuitar={async (setId, itemId) => {
                await sets.quitar.mutateAsync({ setId, itemId });
              }}
              alDesactivar={async (setId) => {
                await sets.desactivar.mutateAsync(setId);
              }}
            />
          </div>
        )}

        {usaItems(f.tipo) && (
          <EditorItems
            plural={rotulo.plural}
            singular={rotulo.singular}
            items={f.items}
            error={campos.items}
            alCambiar={(items) => cambiar('items', items)}
          />
        )}

        {/*
          ── La observación abierta (CC-11, 035) ──
          Una casilla y, si se marca, su rótulo. Va DESPUÉS de las opciones
          porque es lo que aparece debajo de ellas en el teléfono: el orden del
          editor imita el orden de lo que se responde.
        */}
        {admiteObservacion(f.tipo) && (
          <fieldset className="rounded-2xl border border-marino-100 p-4">
            <label className="flex items-start gap-2.5">
              <input
                type="checkbox"
                checked={f.admiteObservacion}
                onChange={(e) => cambiar('admiteObservacion', e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-marino-300
                  text-naranja-500 focus:ring-4 focus:ring-marino-700/10"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-marino-700">
                  Dejar que escriba un comentario
                </span>
                <span className="mt-0.5 block text-xs text-marino-400">
                  Aparece un campo abierto debajo, opcional. Sirve para el «otro» que no está
                  en la lista, o para el «¿por qué?» detrás de una puntuación.
                </span>
              </span>
            </label>

            {f.admiteObservacion && (
              <div className="mt-3">
                <Campo
                  etiqueta="Qué dice el campo (opcional)"
                  value={f.observacionRotulo}
                  onChange={(e) => cambiar('observacionRotulo', e.target.value)}
                />
                {/*
                  ⚠️ Este aviso no es relleno. Lo que se escriba ahí es texto
                  libre sobre el propio jefe, o sea identificable por su
                  contenido en un equipo pequeño: sale por la misma puerta que
                  los textos —con su permiso, la configuración del cliente y el
                  mínimo por grupo— y nunca dentro del conteo de opciones. Quien
                  arma el cuestionario tiene que saberlo antes de pedirlo.
                */}
                <p className="mt-2 text-xs leading-relaxed text-marino-500">
                  Lo que escriban acá <strong className="font-semibold">no se cuenta</strong> con
                  las opciones: se lee aparte, como un texto libre, y le aplican las mismas
                  reglas —el mínimo de respuestas por grupo y la configuración del cliente—.
                </p>
              </div>
            )}
          </fieldset>
        )}

        {usaRango(f.tipo) && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              /*
                Tres tipos comparten estas dos columnas y significan cosas
                distintas en cada uno: en `opcion_multiple` son cuántas opciones
                se pueden marcar, en `numero` el rango del valor y en `barra` el
                recorrido. Una etiqueta genérica obligaría a adivinar cuál de las
                tres es — y «Mínimo a elegir» en una barra no significa nada.
              */
              etiqueta={
                f.tipo === 'numero'
                  ? 'Valor mínimo'
                  : f.tipo === 'barra'
                    ? 'Dónde empieza la barra'
                    : 'Mínimo a elegir'
              }
              type="number"
              value={f.minimo}
              onChange={(e) => cambiar('minimo', e.target.value)}
              error={campos.minimo}
            />
            <Campo
              etiqueta={
                f.tipo === 'numero'
                  ? 'Valor máximo'
                  : f.tipo === 'barra'
                    ? 'Dónde termina'
                    : 'Máximo a elegir'
              }
              type="number"
              value={f.maximo}
              onChange={(e) => cambiar('maximo', e.target.value)}
              error={campos.maximo}
            />
          </div>
        )}

        {/*
          ── Los extremos de la barra ──
          Sin ellos, una barra de 0 a 100 no dice qué significa estar a la
          izquierda: el número solo no es una escala, es una cifra. Son
          opcionales porque a veces el enunciado ya lo dice, pero la ayuda de
          abajo lo advierte en vez de dejar que se descubra al responder.
        */}
        {usaExtremos(f.tipo) && (
          <div>
            <div className="grid gap-3 sm:grid-cols-2">
              {/*
                Sin `placeholder`: `Campo` lo omite de sus props a propósito. Un
                texto gris dentro del campo desaparece al escribir y se confunde
                con un valor ya puesto — los ejemplos van en la ayuda de abajo,
                donde se quedan.
              */}
              <Campo
                etiqueta="Qué significa el extremo izquierdo"
                value={f.etiquetaMin}
                onChange={(e) => cambiar('etiquetaMin', e.target.value)}
                error={campos.etiquetaMin}
              />
              <Campo
                etiqueta="Qué significa el extremo derecho"
                value={f.etiquetaMax}
                onChange={(e) => cambiar('etiquetaMax', e.target.value)}
                error={campos.etiquetaMax}
              />
            </div>
            <p className="mt-1.5 text-xs text-marino-500">
              Por ejemplo «Nada» y «Muchísimo». Sin rótulos, quien responde ve una barra sin
              saber hacia dónde crece.
            </p>
          </div>
        )}

        {!seccion && (
          <label className="flex items-center gap-3 text-sm text-marino-700">
            <input
              type="checkbox"
              checked={f.obligatoria}
              onChange={(e) => cambiar('obligatoria', e.target.checked)}
              className="h-4 w-4 rounded border-marino-300 text-naranja-500 focus:ring-marino-700/15"
            />
            Obligatoria
          </label>
        )}
      </div>

      <div className="mt-5 flex justify-end gap-2 border-t border-marino-100 pt-4">
        <Boton variante="secundario" className="px-3.5 py-2 text-sm" onClick={alCerrar}>
          Cancelar
        </Boton>
        <Boton
          className="px-4 py-2 text-sm"
          cargando={acciones.agregar.isPending || acciones.guardar.isPending}
          onClick={() => void enviar()}
        >
          Guardar
        </Boton>
        {/*
          ── El aviso va EN PANTALLA, no solo al cerrar ──
          Un diálogo al salir llega tarde: informa cuando la persona ya decidió
          irse y la obliga a resolver algo que no sabía que tenía pendiente.
          Verlo antes —mientras escribe— es lo que evita el diálogo.

          `aria-live` para que un lector de pantalla lo anuncie: quien no ve el
          punto naranja tiene el mismo derecho a saber que hay algo sin guardar.
        */}
        {hayCambios && (
          <span
            aria-live="polite"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-naranja-700"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-naranja-500" aria-hidden="true" />
            Sin guardar
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * El primer paso de una pregunta nueva: qué tipo es.
 *
 * Va en el MISMO sitio que el editor —no en un modal— para que el teléfono de al
 * lado siga visible: al elegir el tipo se ve de inmediato dónde va a caer la
 * pregunta en el formulario.
 */
function ElegirTipo({
  alElegir,
  alCancelar,
  hayEdicionEnCurso = false,
}: {
  alElegir: (t: TipoPregunta) => void;
  alCancelar: () => void;
  /** `true` cuando se llegó con «Cambiar tipo» y no desde una pregunta nueva. */
  hayEdicionEnCurso?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-marino-100 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold text-marino-800">¿Qué tipo de pregunta?</h2>
          {/*
            El texto cambia según de dónde se llegó. Decirle «elegirlo ahora
            evita rehacer las opciones» a quien ya escribió media pregunta y solo
            quiere cambiar el tipo es un consejo fuera de lugar; lo que esa
            persona necesita saber es que **no va a perder lo escrito**.
          */}
          <p className="mt-0.5 text-xs text-marino-500">
            {hayEdicionEnCurso
              ? 'Lo que ya escribiste se conserva.'
              : 'Se puede cambiar después, pero elegirlo ahora evita rehacer las opciones.'}
          </p>
        </div>
        <Boton variante="secundario" className="px-3 py-1.5 text-sm" onClick={alCancelar}>
          {hayEdicionEnCurso ? 'Volver' : 'Cancelar'}
        </Boton>
      </div>
      <SelectorTipo tipo={null} alElegir={alElegir} />
    </div>
  );
}

/**
 * ── Miniaturas: qué se ve al responder cada tipo ──
 *
 * Se dibujan con `div`, no con imágenes: pesan cero, heredan el tema y no hay
 * que regenerarlas cuando cambie un color.
 *
 * No pretenden ser exactas — la vista previa del teléfono ya muestra la verdad.
 * Lo que tienen que resolver es **reconocer la forma de un vistazo**: «Matriz de
 * escala» no dice nada, pero tres filas con cinco puntos cada una sí.
 */
function Miniatura({ tipo }: { tipo: TipoPregunta }) {
  const punto = 'h-1.5 w-1.5 rounded-full bg-marino-300';
  const puntoActivo = 'h-1.5 w-1.5 rounded-full bg-naranja-400';
  const linea = 'h-1.5 rounded-full bg-marino-200';

  if (tipo === 'likert') {
    return (
      <div className="space-y-1.5">
        <div className={`${linea} w-full`} />
        <div className="flex items-center justify-between gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className={i === 3 ? puntoActivo : punto} />
          ))}
        </div>
      </div>
    );
  }

  if (tipo === 'matriz_likert') {
    return (
      <div className="space-y-1">
        {[0, 1, 2].map((f) => (
          <div key={f} className="flex items-center gap-1">
            <span className={`${linea} w-1/3 shrink-0`} />
            <div className="flex flex-1 items-center justify-between gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className={i === f + 1 ? puntoActivo : punto} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (tipo === 'opcion_unica' || tipo === 'opcion_multiple') {
    const multiple = tipo === 'opcion_multiple';
    const forma = multiple ? 'rounded-[3px]' : 'rounded-full';
    return (
      <div className="space-y-1.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span
              className={`h-2.5 w-2.5 shrink-0 border ${forma} ${
                (multiple ? i < 2 : i === 1)
                  ? 'border-naranja-500 bg-naranja-400'
                  : 'border-marino-300'
              }`}
            />
            <span className={`${linea} flex-1`} />
          </div>
        ))}
      </div>
    );
  }

  if (tipo === 'barra') {
    return (
      <div className="space-y-1.5">
        <div className="relative h-1.5 rounded-full bg-marino-200">
          <span className="absolute inset-y-0 left-0 w-2/3 rounded-full bg-naranja-400" />
          <span className="absolute -top-1 left-2/3 h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 border-naranja-500 bg-white" />
        </div>
        <div className="flex justify-between">
          <span className="h-1 w-4 rounded-full bg-marino-200" />
          <span className="h-1 w-4 rounded-full bg-marino-200" />
        </div>
      </div>
    );
  }

  if (tipo === 'si_no') {
    return (
      <div className="flex gap-1.5">
        <span className="h-5 flex-1 rounded-lg bg-naranja-400" />
        <span className="h-5 flex-1 rounded-lg border border-marino-200" />
      </div>
    );
  }

  if (tipo === 'texto_corto') {
    return <div className="h-5 rounded-lg border border-marino-200 bg-white" />;
  }

  if (tipo === 'texto_largo') {
    return (
      <div className="h-12 rounded-lg border border-marino-200 bg-white p-1.5">
        <div className={`${linea} w-4/5`} />
        <div className={`${linea} mt-1 w-3/5`} />
      </div>
    );
  }

  if (tipo === 'numero') {
    return (
      <div className="flex h-5 items-center rounded-lg border border-marino-200 bg-white px-1.5">
        <span className="text-[10px] font-bold text-marino-400">123</span>
      </div>
    );
  }

  if (tipo === 'fecha') {
    return (
      <div className="rounded-lg border border-marino-200 bg-white p-1">
        <div className="grid grid-cols-5 gap-0.5">
          {Array.from({ length: 10 }, (_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-[2px] ${i === 6 ? 'bg-naranja-400' : 'bg-marino-100'}`}
            />
          ))}
        </div>
      </div>
    );
  }

  // Sección: no se responde, parte el formulario en páginas.
  return (
    <div className="space-y-1">
      <div className="h-3 rounded bg-marino-700" />
      <div className={`${linea} w-2/3`} />
      <div className={`${linea} w-1/2`} />
    </div>
  );
}

/**
 * El selector de tipo, en tarjetas.
 *
 * ── Por qué dejaron de ser chips (revisión del 2026-09-07) ──
 * Eran diez botoncitos de texto agrupados en cuatro familias, y el cliente lo
 * dijo sin rodeos: «no son nada explícitos, es difícil entender cómo crear cada
 * pregunta». Tenía razón, y el defecto era concreto:
 *
 * · El chip solo mostraba el NOMBRE. «Matriz de escala» o «Selección única» no
 *   describen nada a quien no armó uno antes.
 * · La explicación existía —`TIPO_PREGUNTA[t].ayuda`, y está bien escrita— pero
 *   vivía en un `title`: **un tooltip que en pantalla táctil no existe** y que en
 *   escritorio pide adivinar que hay que pasar el ratón por encima.
 * · Nada mostraba **cómo se ve** la pregunta. El tipo es la decisión que más
 *   cambia el resultado y era la que menos información daba.
 *
 * Ahora cada tipo es una tarjeta con su nombre, su descripción SIEMPRE visible y
 * una miniatura de la forma que va a tener al responderse.
 */
function SelectorTipo({
  tipo,
  alElegir,
  incluirSeccion = false,
}: {
  tipo: TipoPregunta | null;
  alElegir: (t: TipoPregunta) => void;
  incluirSeccion?: boolean;
}) {
  return (
    <fieldset>
      <div className="space-y-4">
        {FAMILIAS_PREGUNTA.map((familia) => {
          const tipos = TIPOS_PREGUNTA.filter(
            (t) =>
              TIPO_PREGUNTA[t].familia === familia.clave &&
              (incluirSeccion || !esSeccion(t)),
          );
          if (tipos.length === 0) return null;
          return (
            <div key={familia.clave}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-marino-400">
                {familia.titulo}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {tipos.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => alElegir(t)}
                    aria-pressed={tipo === t}
                    className={`flex items-start gap-3 rounded-xl border p-3 text-left transition
                      focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
                      ${
                        tipo === t
                          ? 'border-naranja-500 bg-naranja-50/60'
                          : 'border-marino-100 hover:border-marino-300 hover:bg-marino-50/60'
                      }`}
                  >
                    {/* La miniatura primero: es lo que se reconoce sin leer. */}
                    <span className="mt-0.5 w-20 shrink-0" aria-hidden="true">
                      <Miniatura tipo={t} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-marino-800">
                        {TIPO_PREGUNTA[t].titulo}
                      </span>
                      {/* La ayuda, SIEMPRE visible. Antes era un `title`. */}
                      <span className="mt-0.5 block text-xs leading-snug text-marino-500">
                        {TIPO_PREGUNTA[t].ayuda}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * La paleta de acentos, como muestras que se ven — un `<select>` con nombres de
 * colores obliga a imaginárselos.
 */
/**
 * El color de una sección: **la paleta como atajo, y un color libre al lado**
 * (036, D-143).
 *
 * ── Lo que cambió y lo que no ──
 * La paleta era el límite; ahora es el atajo. Los seis siguen ahí porque son los
 * que conviven con la marca sin pensarlo, y al lado va el selector del sistema
 * más un campo de hex para pegar el color exacto de una guía de diseño.
 *
 * ⚠️ **El contraste no se elige, se calcula.** Un hex claro con texto blanco
 * encima deja la cabecera ilegible —es literalmente la regla de D-025 sobre el
 * naranja—, así que `acentoDeColor` decide el color del texto por luminancia y
 * la muestra de acá lo enseña ANTES de guardar. No es un adorno de la vista
 * previa: es la única forma de que quien elige un amarillo vea que el texto se
 * vuelve oscuro y no descubra el problema en el teléfono de un participante.
 */
function SelectorColor({
  valor,
  alElegir,
}: {
  valor: string;
  alElegir: (c: string) => void;
}) {
  const esDePaleta = (COLORES_SECCION as readonly string[]).includes(valor);
  const acento = acentoDeColor(valor);
  /*
    El `<input type=color>` solo entiende hex. Con un nombre de la paleta elegido
    se le pasa el hex de ese nombre, así que abrir el selector arranca desde el
    color que se está viendo y no desde un negro cualquiera.
  */
  const hexActual = esDePaleta ? acento.fuerte : valor;

  const [texto, setTexto] = useState(hexActual);
  useEffect(() => setTexto(hexActual), [hexActual]);

  const aplicarTexto = (v: string) => {
    setTexto(v);
    // Solo se aplica cuando ya es un hex completo: aplicar «#1c» pintaría negro
    // en cada tecla y el campo se volvería imposible de escribir.
    if (HEX_SECCION.test(v.trim())) alElegir(v.trim().toLowerCase());
  };

  return (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold text-marino-700">Color de la sección</legend>

      <div className="flex flex-wrap items-center gap-2">
        {COLORES_SECCION.map((nombre) => {
          const a = ACENTOS[nombre]!;
          const activo = valor === nombre;
          return (
            <button
              key={nombre}
              type="button"
              onClick={() => alElegir(nombre)}
              aria-pressed={activo}
              aria-label={`Color ${nombre}`}
              title={nombre}
              className="grid h-9 w-9 place-items-center rounded-full transition
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
              style={{
                background: a.suave,
                boxShadow: activo ? `0 0 0 3px white, 0 0 0 5px ${a.fuerte}` : undefined,
              }}
            >
              <span className="h-5 w-5 rounded-full" style={{ background: a.fuerte }} />
            </button>
          );
        })}

        <span className="mx-1 h-6 w-px bg-marino-100" aria-hidden="true" />

        {/*
          El selector del sistema. Es un `<input type=color>` nativo a propósito:
          trae la rueda, el cuentagotas y el historial del sistema operativo, y
          un selector propio los pierde los tres.
        */}
        <label
          className="grid h-9 w-9 cursor-pointer place-items-center rounded-full transition
            focus-within:ring-4 focus-within:ring-marino-700/15"
          style={{
            background: esDePaleta ? '#f1f5f9' : acento.suave,
            boxShadow: esDePaleta ? undefined : `0 0 0 3px white, 0 0 0 5px ${acento.fuerte}`,
          }}
          title="Elegir otro color"
        >
          <span className="sr-only">Elegir otro color</span>
          <input
            type="color"
            value={hexActual}
            onChange={(e) => alElegir(e.target.value.toLowerCase())}
            className="h-5 w-5 cursor-pointer rounded-full border-0 bg-transparent p-0
              [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full
              [&::-webkit-color-swatch]:border-0"
          />
        </label>

        <input
          value={texto}
          onChange={(e) => aplicarTexto(e.target.value)}
          maxLength={7}
          aria-label="Color en hexadecimal"
          className="w-24 rounded-full border border-marino-200 bg-white px-3 py-1.5 text-sm
            font-medium uppercase text-marino-800
            focus:outline-none focus:ring-4 focus:ring-marino-700/10"
        />
      </div>

      {/*
        La muestra con el texto encima. Enseña el resultado del cálculo de
        contraste sobre el color elegido, que es lo único que hay que mirar antes
        de guardar un color raro.
      */}
      <div
        className="mt-2.5 flex items-center justify-between rounded-2xl px-4 py-2.5"
        style={{ background: acento.fuerte, color: acento.textoSobreFuerte }}
      >
        <span className="text-xs font-bold uppercase tracking-widest">Así se ve la cabecera</span>
        <span className="text-xs font-semibold opacity-80">
          {esDePaleta ? valor : valor.toUpperCase()}
        </span>
      </div>

      <p className="mt-1.5 text-xs text-marino-400">
        Tiñe la página que abre esta sección: la cabecera, los números y los botones. El color del
        texto se ajusta solo para que se lea.
      </p>
    </fieldset>
  );
}

function EditorItems({
  plural,
  singular,
  items,
  error,
  alCambiar,
}: {
  plural: string;
  singular: string;
  items: string[];
  error?: string | undefined;
  alCambiar: (items: string[]) => void;
}) {
  const cambiarUno = (i: number, texto: string) =>
    alCambiar(items.map((v, j) => (i === j ? texto : v)));
  const Singular = singular.charAt(0).toUpperCase() + singular.slice(1);

  return (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold text-marino-700">{plural}</legend>

      <div className="space-y-2">
        {items.map((texto, i) => (
          // El índice como clave es correcto acá y solo acá: la lista se reordena
          // por posición y el valor vive en este mismo arreglo.
          <div key={i} className="flex items-center gap-1.5">
            <Campo
              etiqueta={`${Singular} ${i + 1}`}
              value={texto}
              onChange={(e) => cambiarUno(i, e.target.value)}
              className="flex-1"
            />
            <BotonIcono
              rotulo={`Quitar ${singular} ${i + 1}`}
              onClick={() => alCambiar(items.filter((_, j) => j !== i))}
            >
              <Equis size={14} />
            </BotonIcono>
          </div>
        ))}
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <button
        type="button"
        onClick={() => alCambiar([...items, ''])}
        className="mt-2.5 text-sm font-semibold text-marino-600 transition hover:text-naranja-600 hover:underline"
      >
        Agregar {singular}
      </button>
    </fieldset>
  );
}
